import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, CheckCheck, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export const NotificationBell = ({ token, user }) => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const userRole = user?.role;

  // ── Endpoints ────────────────────────────────────────────────────────────────

  const getEndpoint = useCallback(() => {
    if (userRole === 'admin' || userRole === 'stationsleitung') return `${API}/api/admin/notifications`;
    if (userRole === 'superadmin') return `${API}/api/superadmin/notifications`;
    if (userRole === 'employee') return `${API}/api/employee/notifications`;
    return `${API}/api/notifications`;
  }, [userRole]);

  const getReadEndpoint = useCallback((id) => {
    if (userRole === 'admin' || userRole === 'stationsleitung') return `${API}/api/admin/notifications/${id}/read`;
    if (userRole === 'superadmin') return `${API}/api/superadmin/notifications/${id}/read`;
    if (userRole === 'employee') return `${API}/api/employee/notifications/${id}/read`;
    return `${API}/api/notifications/${id}/mark-read`;
  }, [userRole]);

  const getMarkAllEndpoint = useCallback(() => {
    if (userRole === 'admin' || userRole === 'stationsleitung') return `${API}/api/admin/notifications/mark-all-read`;
    if (userRole === 'superadmin') return `${API}/api/superadmin/notifications/mark-all-read`;
    if (userRole === 'employee') return `${API}/api/employee/notifications/mark-all-read`;
    return `${API}/api/notifications/mark-all-read`;
  }, [userRole]);

  // ── Fetch: NUR ungelesene laden, Zähler = Listenlänge (immer synchron) ───────

  const fetchNotifications = useCallback(async () => {
    if (!token || !userRole) return;
    setLoading(true);
    try {
      const res = await axios.get(getEndpoint(), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const all = Array.isArray(res.data) ? res.data : (res.data?.notifications || []);
      // Nur ungelesene – Zähler kommt aus dieser Liste (immer synchron!)
      const unread = all.filter(n => !n.is_read).slice(0, 20);
      setNotifications(unread);
    } catch (e) {
      console.error('Notifications fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [token, userRole, getEndpoint]);

  // ── Beim Öffnen neu laden ─────────────────────────────────────────────────────

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen]); // eslint-disable-line

  // Polling alle 2 Minuten wenn offen
  useEffect(() => {
    if (!isOpen) return;
    const iv = setInterval(fetchNotifications, 120000);
    return () => clearInterval(iv);
  }, [isOpen, fetchNotifications]);

  // Beim ersten Laden kurz Badge zählen
  useEffect(() => {
    if (token && userRole) fetchNotifications();
  }, [token, userRole]); // eslint-disable-line

  // ── Klick außerhalb schließt ──────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Routing je nach Notification-Type ────────────────────────────────────────

  const getRoute = (notification) => {
    // Priorität 1: action_url (direkte, korrekte URL vom Backend)
    if (notification.action_url) return notification.action_url;

    // Priorität 2: route-Feld (älteres Feld für direkten Pfad)
    if (notification.route && notification.route.startsWith('/')) return notification.route;

    const t = notification.type || '';
    const isEmployee = userRole === 'employee';

    // TGTG (vor 'order' prüfen, da tgtg_order sonst falsch matcht)
    if (t.includes('tgtg') || t.includes('too_good')) return '/admin/tgtg';

    // Bestellungen / neue Bestellung
    if (t === 'new_order' || t.includes('bestellung'))
      return '/bestellungen/historie';

    // MHD / Ablauf-Produkte  – Superadmin → Produkt-Freigaben
    if (t.includes('mhd') || t.includes('pending_product'))
      return userRole === 'superadmin'
        ? '/superadmin/pending-products'
        : (isEmployee ? '/employee/mhd-control' : '/admin/mhd-control');

    // Temperatur
    if (t.includes('temp') || t.includes('temperatur'))
      return isEmployee ? '/employee/temperature' : '/admin/temperature';

    // Unterweisungen
    if (t.includes('instruction') || t.includes('unterweis'))
      return isEmployee ? '/employee/unterweisungen' : '/admin/unterweisungen';

    // Verbesserungsvorschläge
    if (t.includes('suggestion') || t.includes('improvement'))
      return isEmployee ? '/employee/improvement-suggestions' : '/admin/backshop/vorschlaege';

    // Lieferung & Reklamation (ohne action_url als Fallback)
    if (t.includes('delivery') || t.includes('lieferung') || t.includes('reklamation'))
      return '/lieferung';

    // Lotto
    if (t.includes('lotto'))
      return isEmployee ? '/employee/lotto' : '/admin/lotto';

    // Aufgaben
    if (t.includes('task') || t.includes('aufgabe'))
      return '/admin/task-types';

    // Personal / Mitarbeiter
    if (t.includes('employee_deleted') || t.includes('personal') || t.includes('hr'))
      return '/admin/personal';

    // Trusted Sender (E-Mail-Import Einstellungen)
    if (t.includes('trusted_sender'))
      return '/admin/company';

    // Bestand / Zusatz
    if (t.includes('low_stock') || t.includes('bestand') || t.includes('zusatz'))
      return isEmployee ? '/employee/zusatz' : '/admin/zusatz';

    // Schicht / Shift → kein eigener Notif-Einstiegspunkt, Dashboard
    if (t.includes('shift') || t.includes('schicht'))
      return isEmployee ? '/employee/dashboard' : '/admin/dashboard';

    // Fallback
    return isEmployee ? '/employee/dashboard' : '/admin/dashboard';
  };

  // ── Notification anklicken ────────────────────────────────────────────────────

  const handleClick = async (notification) => {
    // 1. Sofort aus Liste entfernen (Badge sinkt sofort)
    setNotifications(prev => prev.filter(n => n.id !== notification.id));

    // 2. Dropdown schließen
    setIsOpen(false);

    // 3. Weiterleiten: action_url hat Vorrang (direkte Ziel-URL), sonst Typ-Routing
    const route = notification.action_url || getRoute(notification);
    navigate(route);

    // 4. Backend im Hintergrund informieren (non-blocking)
    if (!notification.is_read) {
      axios.put(getReadEndpoint(notification.id), {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }
  };

  // ── Alle als gelesen markieren ────────────────────────────────────────────────

  const markAllRead = async () => {
    setNotifications([]);
    setIsOpen(false);
    axios.put(getMarkAllEndpoint(), {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
  };

  // ── Zeit formatieren ─────────────────────────────────────────────────────────

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'Gerade eben';
    if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
    if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
    return `vor ${Math.floor(diff / 86400)} Tag${Math.floor(diff / 86400) > 1 ? 'en' : ''}`;
  };

  const count = notifications.length;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-muted rounded-lg transition-colors"
        aria-label="Benachrichtigungen"
      >
        <Bell className="h-5 w-5 text-muted-foreground" />
        {count > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[1rem] h-4 px-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">
                Benachrichtigungen
              </span>
              {count > 0 && (
                <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 rounded-full font-medium">
                  {count} neu
                </span>
              )}
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-muted rounded-lg transition-colors"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {/* List */}
          <div className="overflow-y-auto" style={{ maxHeight: '420px' }}>
            {loading ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Lädt…</div>
            ) : count === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-30" />
                <p className="text-sm text-muted-foreground">Keine neuen Benachrichtigungen</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className="flex items-start gap-3 px-4 py-3 border-b border-border last:border-0 cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-1.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground leading-snug">
                      {n.title}
                    </p>
                    {n.message && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {n.message}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      {formatTime(n.created_at)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {count > 0 && (
            <div className="px-4 py-2.5 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllRead}
                className="w-full text-xs text-muted-foreground hover:text-foreground gap-1.5"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Alle als gelesen markieren
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
